import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { commonConstant, SelectOption } from './constants';
import { fetchEntityTypes } from './services/entity.management.service';
import { FetchEntityTypesQueryParams } from './models/EntityType';
import { SelectedFulfillerConfig } from './models/HeaderTypes';
import { isInCometPortal } from './utilities/helper';

export type FulfillerModalConfig = {
  fulfillerData: SelectedFulfillerConfig;
  xCometAccountId?: string;
};

type FulfillerContextProps = {
  fulfillerDetails: FulfillerModalConfig | null;
  setFulfillerDetails: (details: FulfillerModalConfig | null) => void;
  entityTypeOptions: SelectOption[];
  isLoadingEntityTypes: boolean;
  refreshEntityTypes: () => Promise<void>;
};

export const FulfillerContext = createContext<
  FulfillerContextProps | undefined
>(undefined);

const getInitialFulfillerDetails = (): FulfillerModalConfig | null => {
  if (isInCometPortal()) {
    return null;
  }

  try {
    const storedFulfiller = localStorage.getItem(
      commonConstant.FULFILLER_TIMEZONE_PREFERENCE,
    );

    return storedFulfiller ? JSON.parse(storedFulfiller as string) : null;
  } catch {
    return null;
  }
};

export const FulfillerProvider = ({ children }: { children: ReactNode }) => {
  const [fulfillerDetails, setFulfillerDetails] =
    useState<FulfillerModalConfig | null>(getInitialFulfillerDetails);

  const [entityTypeOptions, setEntityTypeOptions] = useState<SelectOption[]>(
    [],
  );

  const [isLoadingEntityTypes, setIsLoadingEntityTypes] = useState(false);

  const tenantId = fulfillerDetails?.fulfillerData?.fulfillerLocation?.value;

  const refreshEntityTypes = useCallback(async () => {
    if (!tenantId) {
      setEntityTypeOptions([]);
      return;
    }

    setIsLoadingEntityTypes(true);
    try {
      const entityTypes = await fetchEntityTypes(
        tenantId,
        {} as FetchEntityTypesQueryParams,
      );
      const options: SelectOption[] = entityTypes.map((et) => ({
        value: et.entityTypeId,
        label: et.entityTypeName,
      }));

      setEntityTypeOptions(options);
    } catch (error) {
      console.error('Failed to fetch entity types:', error);
      setEntityTypeOptions([]);
    } finally {
      setIsLoadingEntityTypes(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refreshEntityTypes();
  }, [refreshEntityTypes]);

  useEffect(() => {
    if (isInCometPortal()) {
      return;
    }

    if (fulfillerDetails) {
      localStorage.setItem(
        commonConstant.FULFILLER_TIMEZONE_PREFERENCE,
        JSON.stringify(fulfillerDetails),
      );
    } else {
      localStorage.removeItem(commonConstant.FULFILLER_TIMEZONE_PREFERENCE);
    }
  }, [fulfillerDetails]);

  return (
    <FulfillerContext.Provider
      value={{
        fulfillerDetails,
        setFulfillerDetails,
        entityTypeOptions,
        isLoadingEntityTypes,
        refreshEntityTypes,
      }}
    >
      {children}
    </FulfillerContext.Provider>
  );
};

// Custom hook to use the fulfiller context in components.
export const useFulfiller = () => {
  const context = useContext(FulfillerContext);

  if (!context) {
    throw new Error('useFulfiller must be used within a FulfillerProvider');
  }

  const {
    fulfillerDetails,
    setFulfillerDetails,
    entityTypeOptions,
    isLoadingEntityTypes,
    refreshEntityTypes,
  } = context;

  return {
    ...fulfillerDetails,
    setFulfillerDetails,
    entityTypeOptions,
    isLoadingEntityTypes,
    refreshEntityTypes,
  };
};
